import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import EditIcon from '@mui/icons-material/EditOutlined'
import CloseIcon from '@mui/icons-material/Close'
import services from '../../services'
import { useNotification } from '../../hooks/useNotification.js'
import Janela from '../componentes/Janela'
import VinculoDialog, { ItemVinculo } from '../componentes/VinculoDialog'
import { plural } from './comum'
import { CONDICOES, Filtro, FiltroForm, filtroVazio, formDoFiltro, fraseDoFiltro, literal, VARIAVEIS } from './filtros'
import './gestao.scss'

interface Vinculo { id: number; groupId: number; group?: { id: number; name: string; environmentId: number }; dashboardRuleId?: number | null; aiAgentId?: number | null }
interface Dashboard { id: number; name: string; type?: string; active: boolean; dashboardGroups?: Vinculo[]; dashboardFilters?: Filtro[] }
interface Regra { id: number; name: string; table: string; column: string }
interface AcessoPessoa { id: number; user?: { id: number; name: string; email: string; company?: string } }
interface AcessoEquipe { id: number; team?: { id: number; name: string; members?: unknown[] } }
interface Visao { environmentName: string; environmentId: number }

/** Gestão › um dashboard num ambiente: grupo no menu, regra, filtros, quem acessa e oneflow. */
export default function DashboardNoAmbiente() {
  const { id, envId } = useParams()
  const ambienteId = Number(envId)
  const navigate = useNavigate()
  const { notify } = useNotification()

  const [dash, setDash] = useState<Dashboard | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [visoes, setVisoes] = useState<Visao[]>([])
  const [grupos, setGrupos] = useState<{ id: number; name: string }[]>([])
  const [regras, setRegras] = useState<Regra[]>([])
  const [agentes, setAgentes] = useState<{ id: number; name: string }[]>([])
  const [pessoas, setPessoas] = useState<AcessoPessoa[]>([])
  const [equipes, setEquipes] = useState<AcessoEquipe[]>([])
  const [buscaAcesso, setBuscaAcesso] = useState('')
  const [darAcesso, setDarAcesso] = useState(false)
  const [todasPessoas, setTodasPessoas] = useState<ItemVinculo[] | null>(null)
  const [todasEquipes, setTodasEquipes] = useState<ItemVinculo[] | null>(null)
  const [filtroAberto, setFiltroAberto] = useState<FiltroForm | null>(null)
  const [salvandoFiltro, setSalvandoFiltro] = useState(false)
  const [remover, setRemover] = useState(false)
  const [ocupado, setOcupado] = useState(false)

  const vinculo = dash?.dashboardGroups?.find(g => g.group?.environmentId === ambienteId)
  const nomeAmbiente = visoes.find(v => v.environmentId === ambienteId)?.environmentName || ''

  const carregarAcessos = useCallback(async (dgId: number) => {
    const [p, e] = await Promise.all([services.api.getAllUsersFromDashGroupId(dgId), services.api.getAllTeamsFromDashGroupId(dgId)])
    setPessoas(p.fail ? [] : (p.data || []).sort((a: AcessoPessoa, b: AcessoPessoa) => (a.user?.name || '').localeCompare(b.user?.name || '')))
    setEquipes(e.fail ? [] : (e.data || []).sort((a: AcessoEquipe, b: AcessoEquipe) => (a.team?.name || '').localeCompare(b.team?.name || '')))
  }, [])

  const carregar = useCallback(async () => {
    const [d, v] = await Promise.all([services.api.getDashboardById(id), services.api.getDashboardEnvOverview(id)])
    if (d.fail || !d.data) { setErro(d.error || 'Dashboard não encontrado.'); return }
    setDash(d.data)
    setVisoes(v.fail ? [] : (v.data || []).sort((a: Visao, b: Visao) => a.environmentName.localeCompare(b.environmentName)))
    const dg = (d.data.dashboardGroups || []).find((g: Vinculo) => g.group?.environmentId === ambienteId)
    if (dg) carregarAcessos(dg.id)
  }, [id, ambienteId, carregarAcessos])

  useEffect(() => {
    setDash(null); setErro(null); setPessoas([]); setEquipes([])
    carregar()
    services.api.getAllEnvGroups(ambienteId).then((r: any) => { if (!r.fail) setGrupos(r.data || []) })
  }, [carregar, ambienteId])

  useEffect(() => {
    services.api.getAllDashboardRules().then((r: any) => { if (!r.fail) setRegras((r.data || []).sort((a: Regra, b: Regra) => a.name.localeCompare(b.name))) })
    services.api.getAllAgents().then((r: any) => { if (!r.fail) setAgentes((r.data || []).sort((a: any, b: any) => a.name.localeCompare(b.name))) })
  }, [])

  const filtros = useMemo(() => (dash?.dashboardFilters || [])
    // Já vêm na ordem gravada (o backend ordena).
    .filter(f => f.environmentId === ambienteId), [dash, ambienteId])

  if (erro) return <div className="ng"><p className="ng-explic">{erro}</p><Link to="/app/gestao/dashboards">Voltar ao catálogo</Link></div>
  if (!dash) return <div className="ng"><p className="ng-explic">Carregando…</p></div>
  if (!vinculo) {
    return (
      <div className="ng">
        <p className="ng-explic">{dash.name} não aparece neste ambiente.</p>
        <Link to={`/app/gestao/dashboards/${dash.id}`}>Ver onde ele aparece</Link>
      </div>
    )
  }

  const dg = vinculo.id
  const regra = regras.find(r => r.id === vinculo.dashboardRuleId)

  // Cada mudança grava na hora, como na tela antiga; depois recarrega o dashboard.
  const gravar = async (acao: Promise<any>, ok: string, falha: string) => {
    const r = await acao
    if (r.fail || (r.status && r.status >= 300)) { notify(r.error || falha, { variant: 'error' }); return false }
    notify(ok, { variant: 'success' })
    await carregar()
    return true
  }

  const trocarGrupo = (groupId: string) =>
    gravar(services.api.updateDashboardGroupGroup(dg, groupId), 'Grupo alterado. O dashboard muda de lugar no menu deste ambiente.', 'Não foi possível trocar o grupo.')
  const trocarRegra = (ruleId: string) =>
    gravar(services.api.updateDashboardGroupRule(dg, ruleId || 0), ruleId ? 'Regra aplicada neste ambiente.' : 'Regra retirada: todos veem o mesmo recorte.', 'Não foi possível trocar a regra.')
  const trocarAgente = (agentId: string) =>
    gravar(services.api.updateDashboardGroupAiAgent(dg, agentId || 0), agentId ? 'oneflow ligado neste ambiente.' : 'oneflow desligado neste ambiente.', 'Não foi possível trocar o agente.')

  const salvarFiltro = async () => {
    if (!filtroAberto) return
    const f = filtroAberto
    const corpo = {
      table: f.tabela.trim(), colunm: f.coluna.trim(), operator: f.condicao, acumulator: 'AND',
      type: f.origem === 'FIXO' ? 'STATIC' : 'VARIABLE',
      value: f.origem === 'FIXO' ? literal(f.condicao, f.valor, f.numero) : f.origem
    }
    setSalvandoFiltro(true)
    const ok = f.id
      ? await gravar(services.api.updateFilterToDashboard({ id: f.id, ...corpo }), 'Filtro alterado.', 'Não foi possível alterar o filtro.')
      : await gravar(services.api.addFilterToDashboard({ ...corpo, order: null, dashboardId: dash.id, environmentId: ambienteId }), 'Filtro adicionado.', 'Não foi possível adicionar o filtro.')
    setSalvandoFiltro(false)
    if (ok) setFiltroAberto(null)
  }

  const tirarFiltro = (f: Filtro) => gravar(services.api.removeFilterFromDashboard(f.id), 'Filtro removido.', 'Não foi possível remover o filtro.')

  const mover = async (i: number, para: number) => {
    const ids = filtros.map(f => f.id)
    ;[ids[i], ids[para]] = [ids[para], ids[i]]
    await gravar(services.api.setDashboardFilterOrder(ids), 'Ordem dos filtros alterada.', 'Não foi possível mudar a ordem.')
  }

  const abrirDarAcesso = () => {
    setDarAcesso(true)
    if (!todasPessoas) {
      services.api.getUsersAll({ orderByColumn: 'name' }, 1, 20000).then((r: any) => setTodasPessoas(r.fail ? [] : (r.data?.data || []).map((u: any) => ({
        id: String(u.id), nome: u.name, sub: [u.email, u.company].filter(Boolean).join(' · '), busca: `${u.email} ${u.company || ''} ${u.username || ''}`
      }))))
    }
    if (!todasEquipes) {
      services.api.getAllTeamsPaged({ orderByColuna: 'name' }, 1, 5000).then((r: any) => setTodasEquipes(r.fail ? [] : (r.data?.data || []).map((t: any) => ({
        id: String(t.id), nome: t.name, sub: t.members ? plural(t.members.length, 'pessoa', 'pessoas') : undefined
      }))))
    }
  }

  const tirarPessoa = (a: AcessoPessoa) => gravar(services.api.removeUserFromDashboardGroup(a.id).then((r: any) => r), `${a.user?.name || 'Pessoa'} perdeu o acesso neste ambiente.`, 'Não foi possível retirar o acesso.').then(() => carregarAcessos(dg))
  const tirarEquipe = (a: AcessoEquipe) => gravar(services.api.removeTeamFromDashboardGroup(a.id).then((r: any) => r), `A equipe ${a.team?.name || ''} perdeu o acesso neste ambiente.`, 'Não foi possível retirar o acesso.').then(() => carregarAcessos(dg))

  const confirmarRemover = async () => {
    setOcupado(true)
    const r = await services.api.deleteDashboardGroup(dash.id, vinculo.groupId)
    setOcupado(false)
    if (r.fail) { notify(r.error || 'Não foi possível remover do ambiente.', { variant: 'error' }); return }
    notify(`${dash.name} foi removido de ${nomeAmbiente}.`, { variant: 'success' })
    navigate(`/app/gestao/dashboards/${dash.id}`)
  }

  const q = buscaAcesso.trim().toLowerCase()
  const equipesVisiveis = equipes.filter(a => !q || (a.team?.name || '').toLowerCase().includes(q))
  const pessoasVisiveis = pessoas.filter(a => !q || `${a.user?.name} ${a.user?.email} ${a.user?.company}`.toLowerCase().includes(q))
  const jaPessoas = new Set(pessoas.map(a => String(a.user?.id)))
  const jaEquipes = new Set(equipes.map(a => String(a.team?.id)))
  const html = dash.type === 'html'

  return (
    <div className="ng">
      <nav className="ng-trilha" aria-label="Caminho">
        <Link to="/app/gestao/dashboards">Dashboards</Link><span>›</span>
        <Link to={`/app/gestao/dashboards/${dash.id}`}>{dash.name}</Link><span>›</span>
        <Link to={`/app/gestao/ambientes/${ambienteId}`}>{nomeAmbiente || 'Ambiente'}</Link>
      </nav>
      <div className="ng-cab">
        <h1>{dash.name} <span className="ng-pill">{nomeAmbiente}</span>{!dash.active && <span className="ng-pill inativo">Inativo</span>}</h1>
        <div className="ng-acoes">
          {visoes.length > 1 && (
            <select className="ng-select" value={ambienteId} aria-label="Ver em outro ambiente" onChange={e => navigate(`/app/gestao/dashboards/${dash.id}/ambiente/${e.target.value}`)}>
              {visoes.map(v => <option key={v.environmentId} value={v.environmentId}>{v.environmentName}</option>)}
            </select>
          )}
          <Link className="ng-btn" to={`/app/dashboard/${dash.id}/${ambienteId}`}>Abrir dashboard</Link>
          <Link className="ng-btn" to={`/app/gestao/dashboards/${dash.id}`}>Configurações gerais</Link>
        </div>
      </div>
      <p className="ng-explic">O que vale só em {nomeAmbiente || 'este ambiente'}. Link, atualização e opções do Power BI ficam nas configurações gerais.</p>

      <div className="ng-duas">
        <section className="ng-cartao">
          <h2>No menu</h2>
          <p className="ng-explic">Em que grupo o dashboard aparece no menu deste ambiente.</p>
          <div className="ng-campos">
            <label className="ng-campo"><span>Grupo</span>
              <select value={vinculo.groupId} onChange={e => trocarGrupo(e.target.value)}>
                {grupos.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                {!grupos.some(g => g.id === vinculo.groupId) && <option value={vinculo.groupId}>{vinculo.group?.name}</option>}
              </select>
            </label>
          </div>
          <div className="ng-rodape-cartao">
            <button type="button" className="ng-btn perigo" onClick={() => setRemover(true)}>Remover deste ambiente</button>
          </div>
        </section>

        <section className="ng-cartao">
          <h2>Regra</h2>
          <p className="ng-explic">Recorta o dado de acordo com quem abre. Quem administra o BI não sofre a regra.</p>
          <div className="ng-campos">
            <label className="ng-campo largo"><span>Regra</span>
              <select value={vinculo.dashboardRuleId || ''} onChange={e => trocarRegra(e.target.value)}>
                <option value="">Nenhuma: todos veem o mesmo recorte</option>
                {regras.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </label>
          </div>
          {regra && (
            <p className="ng-frase">Cada pessoa vê só os valores dela em <code>{regra.table}[{regra.column}]</code>. Os valores de cada pessoa ficam em <Link to={`/app/gestao/regras/${regra.id}`}>Regras › {regra.name}</Link> e valem para todo dashboard que usa a regra.</p>
          )}
        </section>
      </div>

      <section className="ng-cartao">
        <div className="ng-cartao-cab">
          <h2>Filtros neste ambiente</h2>
          <div className="ng-acoes"><button type="button" className="ng-btn" onClick={() => setFiltroAberto(filtroVazio())}><AddIcon />Adicionar filtro</button></div>
        </div>
        <p className="ng-explic">Valem para todas as pessoas, por cima da regra. O valor pode ser fixo ou vir de quem abre (nome, e-mail, CPF, empresa).</p>
        {filtros.length > 0 && <p className="ng-frase">Vale só onde {filtros.map((f, i) => <React.Fragment key={f.id}>{i > 0 && <b> e </b>}{fraseDoFiltro(f)}</React.Fragment>)}.</p>}
        <div className="ng-tabela">
          <table>
            <thead><tr><th>Campo</th><th>Condição</th><th>Valor</th><th /></tr></thead>
            <tbody>
              {filtros.length === 0 && <tr><td colSpan={4} className="vazio">Nenhum filtro: o ambiente vê o relatório inteiro (ou o recorte da regra).</td></tr>}
              {filtros.map((f, i) => (
                <tr key={f.id}>
                  <td><code>{f.table}[{f.colunm}]</code></td>
                  <td>{CONDICOES.find(c => c.valor === f.operator)?.rotulo || f.operator}</td>
                  <td>{f.type === 'VARIABLE' ? <span className="ng-tag">{VARIAVEIS.find(v => v.valor === f.value)?.rotulo || f.value} de quem abre</span> : <code>{f.value}</code>}</td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button type="button" className="ng-ico" disabled={i === 0} onClick={() => mover(i, i - 1)} aria-label="Subir"><ArrowUpIcon fontSize="small" /></button>
                    <button type="button" className="ng-ico" disabled={i === filtros.length - 1} onClick={() => mover(i, i + 1)} aria-label="Descer"><ArrowDownIcon fontSize="small" /></button>
                    <button type="button" className="ng-ico" onClick={() => setFiltroAberto(formDoFiltro(f))} aria-label="Editar filtro"><EditIcon fontSize="small" /></button>
                    <button type="button" className="ng-ico perigo" onClick={() => tirarFiltro(f)} aria-label="Remover filtro"><CloseIcon fontSize="small" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="ng-cartao">
        <div className="ng-cartao-cab">
          <h2>Quem acessa neste ambiente</h2>
          <div className="ng-acoes"><button type="button" className="ng-btn prim" onClick={abrirDarAcesso}><AddIcon />Dar acesso</button></div>
        </div>
        <p className="ng-explic">Equipe dá acesso a todos os membros dela, inclusive a quem entrar depois. {plural(equipes.length, 'equipe', 'equipes')} · {plural(pessoas.length, 'pessoa', 'pessoas')} direto.</p>
        {equipes.length + pessoas.length > 8 && (
          <div className="ng-filtros" style={{ marginBottom: 10 }}>
            <label className="ng-busca"><SearchIcon fontSize="small" />
              <input value={buscaAcesso} onChange={e => setBuscaAcesso(e.target.value)} placeholder="Buscar equipe ou pessoa" aria-label="Buscar equipe ou pessoa" />
            </label>
          </div>
        )}
        <div className="ng-duas">
          <div>
            <h3 className="ng-sub">Equipes</h3>
            <div className="ng-lista">
              {equipes.length === 0 && <p className="ng-explic">Nenhuma equipe.</p>}
              {equipesVisiveis.map(a => (
                <div key={a.id} className="ng-linha">
                  <span className="ng-amb-ini">{(a.team?.name || '?').charAt(0)}</span>
                  <span className="ng-amb-txt"><b>{a.team?.name}</b>{a.team?.members && <small>{plural(a.team.members.length, 'pessoa', 'pessoas')}</small>}</span>
                  <button type="button" className="ng-ico perigo" onClick={() => tirarEquipe(a)} aria-label={`Tirar o acesso da equipe ${a.team?.name}`} title="Tirar o acesso"><CloseIcon fontSize="small" /></button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="ng-sub">Pessoas</h3>
            <div className="ng-lista">
              {pessoas.length === 0 && <p className="ng-explic">Ninguém com acesso direto.</p>}
              {pessoasVisiveis.map(a => (
                <div key={a.id} className="ng-linha">
                  <span className="ng-amb-ini">{(a.user?.name || '?').charAt(0)}</span>
                  <span className="ng-amb-txt"><b>{a.user?.name}</b><small>{[a.user?.email, a.user?.company].filter(Boolean).join(' · ')}</small></span>
                  <button type="button" className="ng-ico perigo" onClick={() => tirarPessoa(a)} aria-label={`Tirar o acesso de ${a.user?.name}`} title="Tirar o acesso"><CloseIcon fontSize="small" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="ng-cartao">
        <h2><span className="ng-oneflow"><b>one</b><i>flow</i> IA</span></h2>
        <p className="ng-explic">Perguntas sobre este dashboard, respondidas com a visão de quem pergunta.{html ? ' Em relatório HTML o oneflow ainda não aparece.' : ''}</p>
        <div className="ng-campos">
          <label className="ng-campo"><span>Agente</span>
            <select value={vinculo.aiAgentId || ''} onChange={e => trocarAgente(e.target.value)}>
              <option value="">Desligado: o botão do oneflow não aparece aqui</option>
              {agentes.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <small>As instruções e as perguntas sugeridas são do agente e valem em todo dashboard que usa ele.</small>
          </label>
        </div>
        {vinculo.aiAgentId && <p className="ng-explic" style={{ marginTop: 10 }}><Link to={`/app/gestao/agentes/${vinculo.aiAgentId}`}>Editar o agente ›</Link></p>}
      </section>

      <Janela aberto={!!filtroAberto} titulo={filtroAberto?.id ? 'Editar filtro' : 'Adicionar filtro'} sub={`${dash.name} · ${nomeAmbiente}`}
              botao={filtroAberto?.id ? 'Salvar filtro' : 'Adicionar filtro'} salvando={salvandoFiltro}
              desabilitado={!filtroAberto || !filtroAberto.tabela.trim() || !filtroAberto.coluna.trim() || (filtroAberto.origem === 'FIXO' && !filtroAberto.valor.trim())}
              aoConfirmar={salvarFiltro} aoFechar={() => setFiltroAberto(null)}>
        {filtroAberto && <CamposDoFiltro f={filtroAberto} mudar={setFiltroAberto} />}
      </Janela>

      <Janela aberto={remover} titulo="Remover deste ambiente" botao="Remover do ambiente" perigo salvando={ocupado}
              aoConfirmar={confirmarRemover} aoFechar={() => setRemover(false)}>
        <p style={{ margin: 0, lineHeight: 1.5 }}>Remover <b>{dash.name}</b> de <b>{nomeAmbiente}</b>? Ele some do menu de todos ali, e os acessos, a regra e os filtros deste ambiente se perdem. Nos outros ambientes nada muda.</p>
      </Janela>

      <VinculoDialog
        aberto={darAcesso}
        titulo="Dar acesso"
        sub={`${dash.name} · ${nomeAmbiente}`}
        carregando={!todasPessoas || !todasEquipes}
        abas={[
          { id: 'eq', rotulo: 'Equipes', dica: 'Buscar equipe', unidade: ['equipe', 'equipes'], itens: (todasEquipes || []).map(i => ({ ...i, ja: jaEquipes.has(i.id) ? 'Já tem' : undefined })) },
          { id: 'ps', rotulo: 'Pessoas', dica: 'Buscar por nome, e-mail ou empresa', unidade: ['pessoa', 'pessoas'], itens: (todasPessoas || []).map(i => ({ ...i, ja: jaPessoas.has(i.id) ? 'Já tem' : undefined })) }
        ]}
        resumo={(sel, total) => total
          ? <>{[sel.eq?.length ? plural(sel.eq.length, 'equipe', 'equipes') : '', sel.ps?.length ? plural(sel.ps.length, 'pessoa', 'pessoas') : ''].filter(Boolean).join(' e ')} {total === 1 ? 'passa' : 'passam'} a ver <b>{dash.name}</b> em <b>{nomeAmbiente}</b>{regra ? <>, com o recorte da regra <b>{regra.name}</b></> : null}.</>
          : <>Escolha equipes ou pessoas. Equipe é melhor: quem entrar nela depois já ganha o acesso.</>}
        botao={total => total ? `Dar acesso a ${total}` : 'Dar acesso'}
        aoFechar={() => setDarAcesso(false)}
        aoConfirmar={async sel => {
          const eq = (sel.eq || []).map(i => Number(i.id)), ps = (sel.ps || []).map(i => Number(i.id))
          if (eq.length) { const r = await services.api.addDashboardTeams(eq, dg); if (r.fail) return r.error || 'Não foi possível dar acesso às equipes.' }
          if (ps.length) { const r = await services.api.addDashboardUsers(ps, dg); if (r.fail) return r.error || 'Não foi possível dar acesso às pessoas.' }
          setDarAcesso(false)
          notify('Acesso dado.', { variant: 'success' })
          carregarAcessos(dg)
          return null
        }}
      />
    </div>
  )
}

/** Os campos do filtro, com a prévia do que vai para o Power BI. */
function CamposDoFiltro({ f, mudar }: { f: FiltroForm; mudar: (f: FiltroForm) => void }) {
  const fixo = f.origem === 'FIXO'
  return (
    <>
      <div className="nv-campos">
        <label className="nv-campo"><span>Tabela</span><input value={f.tabela} onChange={e => mudar({ ...f, tabela: e.target.value })} placeholder="Ex.: dCliente" /></label>
        <label className="nv-campo"><span>Coluna</span><input value={f.coluna} onChange={e => mudar({ ...f, coluna: e.target.value })} placeholder="Ex.: GRUPO" /></label>
      </div>
      <div className="nv-campos">
        <label className="nv-campo"><span>Condição</span>
          <select value={f.condicao} onChange={e => mudar({ ...f, condicao: e.target.value })}>
            {CONDICOES.map(c => <option key={c.valor} value={c.valor}>{c.rotulo}</option>)}
          </select>
        </label>
        <label className="nv-campo"><span>Valor</span>
          <select value={f.origem} onChange={e => mudar({ ...f, origem: e.target.value })}>
            <option value="FIXO">Fixo</option>
            {VARIAVEIS.map(v => <option key={v.valor} value={v.valor}>{v.rotulo} de quem abre</option>)}
          </select>
        </label>
      </div>
      {fixo && (
        <label className="nv-campo">
          <span>{f.condicao === 'IN' ? 'Valores (separe por vírgula)' : 'Valor fixo'}</span>
          <input value={f.valor} onChange={e => mudar({ ...f, valor: e.target.value })} placeholder={f.condicao === 'IN' ? 'Ex.: SP, RJ, MG' : 'Ex.: SP'} />
          <label className="ng-toggle" style={{ fontSize: 13.5 }}><input type="checkbox" checked={f.numero} onChange={e => mudar({ ...f, numero: e.target.checked })} />É número (sem aspas)</label>
        </label>
      )}
      {f.tabela.trim() && f.coluna.trim() && (fixo ? f.valor.trim() : true) && (
        <p className="nv-resumo" style={{ margin: 0 }}>
          Vai para o Power BI como <code>{f.tabela.trim()}/{f.coluna.trim()} {f.condicao.toLowerCase()} {fixo ? literal(f.condicao, f.valor, f.numero) : `'${VARIAVEIS.find(v => v.valor === f.origem)?.exemplo}'`}</code>
          {!fixo && <> (o valor de quem abre, em maiúsculas)</>}.
        </p>
      )}
    </>
  )
}
